import React, { useEffect, useRef, useState } from "react";
import ModalView from "../../common/modal";
import DropdownComponent from "../../common/dropdown-input";
import LazyEntitySelect from "../../common/multi-select-input/GenericAsyncSelect";
import Text from "../../common/typography/text";
import Button from "../../common/button/button";
import PastDueAlert from "../../common/past-due-alert";
import { useNavigate } from "react-router-dom";
import Tags from "../../common/tags";
import RequiredIcon from "../../icons/required-icon";
import APIService from "../../Service/APIService";
import FailureIcon from "../../icons/failure-icon";
import "./DirectOrder.css";

const hasQuickbooksID = (option) =>
  typeof option?.QuickbooksID === "string"
    ? option.QuickbooksID.trim().length > 0
    : option?.QuickbooksID != null;

const canEvaluateQuickbooksBlocking = (option) =>
  Boolean(option?.value) &&
  Object.prototype.hasOwnProperty.call(option ?? {}, "QuickbooksID");

const getShouldBlockStartOrderForQuickbooks = (option) =>
  canEvaluateQuickbooksBlocking(option) ? !hasQuickbooksID(option) : false;

const normalizeShipToOption = (option) => {
  if (option?.value == null || !option?.label) {
    return null;
  }

  return {
    value: option.value,
    label: option.label,
    QuickbooksID: option?.QuickbooksID ?? null,
    shouldBlockStartOrderForQuickbooks:
      option?.shouldBlockStartOrderForQuickbooks ??
      getShouldBlockStartOrderForQuickbooks(option),
    PastDueWarning: option?.PastDueWarning ?? null,
    PastDueLimit: option?.PastDueLimit ?? null,
    PastDueBalance: option?.PastDueBalance ?? 0,
    IsPastDueWarningExceeded: option?.IsPastDueWarningExceeded === true,
    IsPastDueLimitExceeded: option?.IsPastDueLimitExceeded === true,
    AddressLine1: option?.AddressLine1 ?? null,
    AddressLine2: option?.AddressLine2 ?? null,
    City: option?.City ?? null,
    State: option?.State ?? null,
    Zip: option?.Zip ?? null,
    Phone: option?.Phone ?? null,
    Fax: option?.Fax ?? null,
    DistributorID: option?.DistributorID ?? null,
    DistributorName: option?.DistributorName ?? null,
    IsDistUserAssigned: Boolean(option?.IsDistUserAssigned),
    DistributorUsername: option?.DistributorUsername ?? null,
  };
};

const extractShipToAccountDetails = (option) => ({
  QuickbooksID: option?.QuickbooksID ?? null,
  shouldBlockStartOrderForQuickbooks: Boolean(
    option?.shouldBlockStartOrderForQuickbooks,
  ),
  PastDueWarning: option?.PastDueWarning ?? null,
  PastDueLimit: option?.PastDueLimit ?? null,
  PastDueBalance: option?.PastDueBalance ?? 0,
  IsPastDueWarningExceeded: option?.IsPastDueWarningExceeded === true,
  IsPastDueLimitExceeded: option?.IsPastDueLimitExceeded === true,
  AddressLine1: option?.AddressLine1 ?? null,
  AddressLine2: option?.AddressLine2 ?? null,
  City: option?.City ?? null,
  State: option?.State ?? null,
  Zip: option?.Zip ?? null,
  Phone: option?.Phone ?? null,
  Fax: option?.Fax ?? null,
  DistributorID: option?.DistributorID ?? null,
  DistributorName: option?.DistributorName ?? null,
  IsDistUserAssigned: Boolean(option?.IsDistUserAssigned),
  DistributorUsername: option?.DistributorUsername ?? null,
});

const buildShipToOptions = (selectedParentAccount, relatedAccountOptions = []) => {
  const mergedOptions = [];
  const seenValues = new Set();

  const addOption = (option) => {
    const normalizedOption = normalizeShipToOption(option);
    if (!normalizedOption || seenValues.has(normalizedOption.value)) {
      return;
    }

    seenValues.add(normalizedOption.value);
    mergedOptions.push(normalizedOption);
  };

  addOption(selectedParentAccount);
  relatedAccountOptions.forEach(addOption);

  return mergedOptions;
};

const normalizeCaseIdOption = (option) => {
  const caseId = String(option?.CaseID ?? option?.label ?? "").trim();
  if (!caseId) {
    return null;
  }

  return {
    value:
      option?.value ??
      [
        caseId,
        option?.ManufacturerID ?? "",
        option?.ProductGroupID ?? "",
      ].join("|"),
    label: option?.label || caseId,
    CaseID: caseId,
    ProductGroupID:
      option?.ProductGroupID != null ? Number(option.ProductGroupID) : null,
    ProductGroupName: option?.ProductGroupName ?? null,
    ManufacturerID:
      option?.ManufacturerID != null ? Number(option.ManufacturerID) : null,
    ManufacturerName: option?.ManufacturerName ?? null,
  };
};

const NewDirectOrderModal = ({ isDirectOrder, setIsDirectOrder }) => {
  const navigate = useNavigate();
  // Direct Order Values
  const [selectedParentAccount, setSelectedParentAccount] = useState();
  const [selectedShipToAccount, setSelectedShipToAccount] = useState();
  const [selectedCaseID, setSelectedCaseID] = useState();
  const [caseIDOptions, setCaseIDOptions] = useState([]);
  const [shipToOptions, setShipToOptions] = useState([]);
  const [isLoadingShipTo, setIsLoadingShipTo] = useState(false);
  const [isLoadingCaseIDs, setIsLoadingCaseIDs] = useState(false);
  const [isOldOrderFormPromptOpen, setIsOldOrderFormPromptOpen] =
    useState(false);
  const relatedAccountsCacheRef = useRef({});
  const caseIdsCacheRef = useRef({});
  const requestIdRef = useRef(0);
  const caseIdRequestIdRef = useRef(0);

  const resetModalState = () => {
    requestIdRef.current += 1;
    caseIdRequestIdRef.current += 1;
    relatedAccountsCacheRef.current = {};
    caseIdsCacheRef.current = {};
    setSelectedParentAccount(undefined);
    setSelectedShipToAccount(undefined);
    setSelectedCaseID(undefined);
    setShipToOptions([]);
    setCaseIDOptions([]);
    setIsLoadingShipTo(false);
    setIsLoadingCaseIDs(false);
  };

  const selectedAccountID = selectedParentAccount?.value;
  const shouldBlockStartOrderForQuickbooks =
    selectedParentAccount?.shouldBlockStartOrderForQuickbooks ??
    getShouldBlockStartOrderForQuickbooks(selectedParentAccount);
  const shouldBlockStartOrderForShipToQuickbooks =
    selectedShipToAccount?.shouldBlockStartOrderForQuickbooks ??
    getShouldBlockStartOrderForQuickbooks(selectedShipToAccount);
  const isPastDueWarningExceeded =
    selectedShipToAccount?.IsPastDueWarningExceeded === true;
  const isPastDueLimitExceeded =
    selectedShipToAccount?.IsPastDueLimitExceeded === true;
  const shouldShowPastDueWarning =
    isPastDueWarningExceeded && !isPastDueLimitExceeded;
  const selectedShipToPastDueWarning =
    selectedShipToAccount?.PastDueWarning ?? null;
  const selectedShipToPastDueLimit =
    selectedShipToAccount?.PastDueLimit ?? null;
  const isStartOrderDisabled =
    !selectedParentAccount?.value ||
    !selectedShipToAccount?.value ||
    shouldBlockStartOrderForQuickbooks ||
    shouldBlockStartOrderForShipToQuickbooks ||
    isPastDueLimitExceeded;

  useEffect(() => {
    if (!isDirectOrder) {
      resetModalState();
    }
    // `resetModalState` is intentionally excluded to avoid re-running this
    // effect from function identity changes while the modal is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirectOrder]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      resetModalState();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (!selectedAccountID) {
      setSelectedShipToAccount(undefined);
      setShipToOptions([]);
      setIsLoadingShipTo(false);
      return;
    }

    setSelectedShipToAccount(undefined);

    const cachedOptions = relatedAccountsCacheRef.current[selectedAccountID];
    if (cachedOptions) {
      setShipToOptions(
        buildShipToOptions(
          cachedOptions?.selectedAccountOption || selectedParentAccount,
          cachedOptions?.relatedOptions,
        ),
      );
      setIsLoadingShipTo(false);
      return;
    }

    const currentRequestId = requestIdRef.current + 1;
    requestIdRef.current = currentRequestId;
    let isCancelled = false;
    setShipToOptions([]);

    const fetchRelatedAccounts = async () => {
      setIsLoadingShipTo(true);
      try {
        const apiService = new APIService();
        const response = await apiService.getDataFromBackend(
          `newdirectorder/relatedaccountoptions?accountID=${encodeURIComponent(
            selectedAccountID,
          )}`,
        );

        const items = Array.isArray(response?.data?.items)
          ? response.data.items
          : [];
        const normalizedSelectedAccount = normalizeShipToOption(
          response?.data?.selectedAccount,
        );
        const normalizedOptions = items
          .map(normalizeShipToOption)
          .filter(Boolean);

        relatedAccountsCacheRef.current[selectedAccountID] = {
          selectedAccountOption: normalizedSelectedAccount,
          relatedOptions: normalizedOptions,
        };

        if (!isCancelled && requestIdRef.current === currentRequestId) {
          setShipToOptions(
            buildShipToOptions(
              normalizedSelectedAccount || selectedParentAccount,
              normalizedOptions,
            ),
          );
        }
      } catch (error) {
        console.error("[NewDirectOrderModal] Failed to load related accounts", error);
        if (!isCancelled && requestIdRef.current === currentRequestId) {
          setShipToOptions(buildShipToOptions(selectedParentAccount, []));
        }
      } finally {
        if (!isCancelled && requestIdRef.current === currentRequestId) {
          setIsLoadingShipTo(false);
        }
      }
    };

    fetchRelatedAccounts();

    return () => {
      isCancelled = true;
    };
  }, [selectedAccountID]);

  useEffect(() => {
    const selectedShipToAccountID = selectedShipToAccount?.value;

    setSelectedCaseID(undefined);
    setCaseIDOptions([]);
    setIsLoadingCaseIDs(false);

    if (!selectedShipToAccountID) {
      return;
    }

    const cachedOptions = caseIdsCacheRef.current[selectedShipToAccountID];
    if (cachedOptions) {
      setCaseIDOptions(cachedOptions);
      return;
    }

    const currentRequestId = caseIdRequestIdRef.current + 1;
    caseIdRequestIdRef.current = currentRequestId;
    let isCancelled = false;

    const fetchCaseIdOptions = async () => {
      setIsLoadingCaseIDs(true);
      try {
        const apiService = new APIService();
        const response = await apiService.getDataFromBackend(
          `newdirectorder/caseidoptions?accountID=${encodeURIComponent(
            selectedShipToAccountID,
          )}`,
        );

        const normalizedOptions = Array.isArray(response?.data?.items)
          ? response.data.items.map(normalizeCaseIdOption).filter(Boolean)
          : [];

        caseIdsCacheRef.current[selectedShipToAccountID] = normalizedOptions;

        if (!isCancelled && caseIdRequestIdRef.current === currentRequestId) {
          setCaseIDOptions(normalizedOptions);
        }
      } catch (error) {
        console.error("[NewDirectOrderModal] Failed to load Case IDs", error);
        if (!isCancelled && caseIdRequestIdRef.current === currentRequestId) {
          setCaseIDOptions([]);
        }
      } finally {
        if (!isCancelled && caseIdRequestIdRef.current === currentRequestId) {
          setIsLoadingCaseIDs(false);
        }
      }
    };

    fetchCaseIdOptions();

    return () => {
      isCancelled = true;
    };
  }, [selectedShipToAccount?.value]);

  const handleStartOrder = () => {
    if (isStartOrderDisabled) {
      return;
    }

    setIsDirectOrder(false);
    // add accountId to params
    navigate("/NewDirectOrder", {
      state: {
        AccountID: selectedParentAccount?.value,
        AccountName: selectedParentAccount?.label,
        ShipToAccountID: selectedShipToAccount?.value,
        ShipTo: selectedShipToAccount?.label,
        ShipToAccountDetails: extractShipToAccountDetails(selectedShipToAccount),
        CaseID: selectedCaseID?.CaseID || "",
        SelectedCase: selectedCaseID
          ? {
            CaseID: selectedCaseID.CaseID,
            ProductGroupID: selectedCaseID.ProductGroupID,
            ProductGroupName: selectedCaseID.ProductGroupName,
            ManufacturerID: selectedCaseID.ManufacturerID,
            ManufacturerName: selectedCaseID.ManufacturerName,
          }
          : null,
      },
    });
  };

  const handleClose = () => {
    setIsDirectOrder(false);
  };

  const handleOpenOldOrderFormPrompt = () => {
    setIsOldOrderFormPromptOpen(true);
  };

  const handleCloseOldOrderFormPrompt = () => {
    setIsOldOrderFormPromptOpen(false);
  };

  const handleGoToOldOrderForm = () => {
    setIsOldOrderFormPromptOpen(false);
    setIsDirectOrder(false);
    navigate("/DirectOrder");
  };

  return (
    <>
      <ModalView
        isOpen={isDirectOrder}
        onClose={handleClose}
        headerRightContent={
          <span
            className="old-order-form-link"
            role="button"
            tabIndex={0}
            onClick={handleOpenOldOrderFormPrompt}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleOpenOldOrderFormPrompt();
              }
            }}
          >
            <Text variant="smallText">Looking for the old order form?</Text>
          </span>
        }
        modalTitle={
          <div className="direct-order-model-top">
            <Text variant="h1">New Order</Text>
            <Tags
              tagBackground="blueBackground"
              textColor="primaryColor"
              tagText="Direct"
            />
          </div>
        }
        textInputComponent={
          <div className="modern-order-form">
            <div className="modern-form-section">
              <div className="modern-field">
                <label className="modern-label required">
                  <Text variant="tinyText">Account</Text>
                  <RequiredIcon />
                </label>

                <LazyEntitySelect
                  value={selectedParentAccount}
                  onChange={setSelectedParentAccount}
                  placeholder="Select account"
                  apiPath="/newdirectorder/accountoptions"
                  pageSize={25}
                  isMulti={false}
                  mapItem={(item) => ({
                    value: item?.value,
                    label: item?.label,
                    QuickbooksID: item?.QuickbooksID ?? null,
                    shouldBlockStartOrderForQuickbooks:
                      getShouldBlockStartOrderForQuickbooks({
                        value: item?.value,
                        QuickbooksID: item?.QuickbooksID ?? null,
                      }),
                  })}
                />
              </div>

              {shouldBlockStartOrderForQuickbooks && (
                <div className="direct-order-modal-warning-banner">
                  <FailureIcon
                    className="warning-icon-container"
                    width={16}
                    height={16}
                  />
                  <Text>
                    Order cannot be placed because the selected account is missing
                    required QuickBooks information.
                  </Text>
                </div>
              )}

              <div className="modern-field">
                <DropdownComponent
                  label="Ship To"
                  placeholder={
                    !selectedAccountID
                      ? "Select account first"
                      : isLoadingShipTo
                        ? "Loading related accounts..."
                        : shipToOptions?.length > 0
                          ? "Select shipping account"
                          : "No related accounts available"
                  }
                  isRequired
                  options={shipToOptions}
                  value={selectedShipToAccount}
                  setValue={setSelectedShipToAccount}
                  isDisabled={
                    !selectedAccountID ||
                    isLoadingShipTo ||
                    shipToOptions.length === 0
                  }
                />

                {shouldBlockStartOrderForShipToQuickbooks && (
                  <div className="direct-order-modal-warning-banner">
                    <FailureIcon
                      className="warning-icon-container"
                      width={16}
                      height={16}
                    />
                    <Text>
                      Order cannot be placed because the selected Ship To account is missing required information. Please contact orders@AdvancedSolution.health
                    </Text>
                  </div>
                )}
              </div>

              {(shouldShowPastDueWarning || isPastDueLimitExceeded) && (
                <div className="modern-field" style={{ width: "100%" }}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "16px",
                      width: "100%",
                    }}
                  >
                    {shouldShowPastDueWarning && (
                      <PastDueAlert
                        variant="warning"
                        thresholdValue={selectedShipToPastDueWarning}
                      />
                    )}
                    {isPastDueLimitExceeded && (
                      <PastDueAlert
                        variant="limit"
                        thresholdValue={selectedShipToPastDueLimit}
                      />
                    )}
                  </div>
                </div>
              )}

              <div className="modern-field">
                <DropdownComponent
                  label={
                    <div className="optional-label">
                      <Text variant="tinyText">Case ID</Text>
                      <Text variant="tinyText" color="lightText">
                        Optional
                      </Text>
                    </div>
                  }
                  placeholder={
                    !selectedShipToAccount?.value
                      ? "Select Ship To first"
                      : isLoadingCaseIDs
                        ? "Loading case IDs..."
                        : caseIDOptions.length > 0
                          ? "Select case ID"
                          : "No case IDs available"
                  }
                  options={caseIDOptions}
                  value={selectedCaseID}
                  setValue={setSelectedCaseID}
                  isDisabled={
                    !selectedShipToAccount?.value ||
                    isLoadingCaseIDs ||
                    caseIDOptions.length === 0
                  }
                />
              </div>
            </div>
          </div>
        }
        footerButtonWrapper={
          <div className="footer-button-wrapper-flex-end">
            <Button
              variant="table"
              subVariant="primary"
              onClick={handleStartOrder}
              disabled={isStartOrderDisabled}
            >
              Start Order
            </Button>
          </div>
        }
      />

      <ModalView
        isOpen={isOldOrderFormPromptOpen}
        onClose={handleCloseOldOrderFormPrompt}
        minWidth="420px"
        maxWidth="520px"
        modalTitle="Try the New Order Form"
        textInputComponent={
          <Text>
            We've streamlined the ordering process and made it much easier to
            place orders with the new order form.
          </Text>
        }
        footerButtonWrapper={
          <div className="footer-button-wrapper-flex-end old-order-form-modal-actions">
            <Button
              variant="table"
              subVariant="outline"
              onClick={handleGoToOldOrderForm}
            >
              Old Order Form
            </Button>
            <Button
              variant="table"
              subVariant="primary"
              onClick={handleCloseOldOrderFormPrompt}
            >
              Try New Order Form
            </Button>
          </div>
        }
      />
    </>
  );
};

export default NewDirectOrderModal;
